import { Box, Button, Paper, Typography, Stack, TextField, Divider } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import GitHubIcon from '@mui/icons-material/GitHub';
import { useTranslation } from '@frontend/i18n';
import { useState } from 'react';

function Login() {
  const t = useTranslation();
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleProviderLogin = (provider: string) => {
    window.location.href = `/api/v1/auth/${provider}`;
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch('/api/v1/auth/password/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        setError(errorText || 'Login failed');
        setLoading(false);
        return;
      }

      window.location.href = '/';
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred during login');
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Paper
        elevation={2}
        sx={{
          maxWidth: 440,
          width: '100%',
          p: 5,
          mx: 2,
          borderRadius: 3,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
            {t.login.title}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            {t.login.subtitle}
          </Typography>
        </Box>

        {!showPasswordLogin ? (
          <>
            <Stack spacing={2.5}>
              <Button
                onClick={() => handleProviderLogin('google')}
                variant="outlined"
                size="large"
                fullWidth
                startIcon={<GoogleIcon />}
                sx={{
                  py: 1.75,
                  borderWidth: 1.5,
                  '&:hover': {
                    borderWidth: 1.5,
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {t.login.continueWithGoogle}
              </Button>
              <Button
                onClick={() => handleProviderLogin('github')}
                variant="outlined"
                size="large"
                fullWidth
                startIcon={<GitHubIcon />}
                sx={{
                  py: 1.75,
                  borderWidth: 1.5,
                  '&:hover': {
                    borderWidth: 1.5,
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {t.login.continueWithGithub}
              </Button>
            </Stack>

            <Divider sx={{ my: 3 }}>{t.login.or}</Divider>

            <Button
              onClick={() => setShowPasswordLogin(true)}
              variant="contained"
              size="large"
              fullWidth
              sx={{
                py: 1.75,
                bgcolor: 'grey.800',
                '&:hover': {
                  bgcolor: 'grey.900',
                },
              }}
            >
              {t.login.signInWithPassword}
            </Button>
          </>
        ) : (
          <>
            <Box component="form" onSubmit={handlePasswordLogin}>
              <Stack spacing={2.5}>
                {error && (
                  <Typography color="error" variant="body2" sx={{ textAlign: 'center' }}>
                    {error}
                  </Typography>
                )}
                <TextField
                  fullWidth
                  label={t.login.email}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  autoFocus
                />
                <TextField
                  fullWidth
                  label={t.login.password}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={loading}
                  sx={{ py: 1.75 }}
                >
                  {loading ? t.login.signingIn : t.login.signIn}
                </Button>
              </Stack>
            </Box>

            <Divider sx={{ my: 3 }}>{t.login.or}</Divider>

            <Button
              onClick={() => {
                setShowPasswordLogin(false);
                setError(null);
                setEmail('');
                setPassword('');
              }}
              variant="outlined"
              size="large"
              fullWidth
              sx={{ py: 1.75 }}
            >
              {t.login.signInWithOAuth}
            </Button>
          </>
        )}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            <a href="https://www.pipelet.ai/terms-of-service">Terms of Service</a> |{' '}
            <a href="https://www.pipelet.ai/privacy-policy">Privacy Policy</a>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}

export default Login;
